import React, { useCallback, useMemo } from 'react';
import {
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { showAlert, showConfirm } from '@/utils/common/alert';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { BaseAdminScreen, ActionButtons } from '@/components';
import { ADMIN_COLORS, DESKTOP_CONFIRM_COPY } from '@/constants/admin';
import { useCreateResourceMutation, convertToFormData } from '@/store/api/admin/resourcesApi';
import { normalizeFilesForUpload } from '@/utils/admin/files';
import { IFile, IResourceFormValues } from '@/types/admin/resources';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { useCreateResourceForm } from '@/components/admin/resources/CreateResource/hooks/useCreateResourceForm';
import { 
  ResourceDetailsSection,
  ResourceContentSection,
  ResourceFilesSection,
} from '@/components/admin/resources/CreateResource/components';

type CreateResourceScreenProps = StackScreenProps<AdminStackParamList, 'CreateResource'>;

export const CreateResourceScreen: React.FC<CreateResourceScreenProps> = ({
  navigation,
  route,
}) => {
  const { highestPosition } = route.params || {};
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  
  const [createResource, { isLoading }] = useCreateResourceMutation();

  // Custom hook for form logic
  const {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    showDiscardAlert,
  } = useCreateResourceForm((highestPosition !== undefined ? highestPosition + 1 : 0));

  // Form submission handler
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    if (!selectedPropertyId) {
      showAlert('Error', 'No property selected');
      return;
    }

    try {
      // Normalize files to IFile[] for type compatibility and ensure web parts
      const normalizedFiles: IFile[] = await normalizeFilesForUpload(formData.files || []);

      const adjusted: IResourceFormValues = {
        ...formData,
        files: normalizedFiles,
      } as unknown as IResourceFormValues;

      // Convert form data to FormData for API
      const resourceFormData = convertToFormData(adjusted, selectedPropertyId);
      
      const result = await createResource(resourceFormData).unwrap();
      
      if (result?.success !== false) {
        showSuccess('Resource created successfully!');
        navigation.goBack();
      }
    } catch (error: any) {
      console.error('Create resource error:', error);
      showApiError(error, 'Failed to create resource');
    }
  }, [formData, validateForm, selectedPropertyId, createResource, showSuccess, showApiError, navigation]);

  // Double-confirm discard on cancel
  const confirmDiscardTwice = useCallback(async (onConfirm: () => void) => {
    const confirmed = await showConfirm(
      DESKTOP_CONFIRM_COPY.discardTitle,
      DESKTOP_CONFIRM_COPY.discardMessage,
      DESKTOP_CONFIRM_COPY.discardConfirmText,
      DESKTOP_CONFIRM_COPY.discardCancelText
    );
    
    if (confirmed) {
      onConfirm();
    }
  }, []);

  const handleCancel = useCallback(() => {
    confirmDiscardTwice(() => navigation.goBack());
  }, [confirmDiscardTwice, navigation]);

  // Memoized scroll content to prevent unnecessary re-renders
  const scrollContent = useMemo(() => (
    <>
      <ResourceDetailsSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />

      <ResourceContentSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />

      <ResourceFilesSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />
    </>
  ), [
    formData,
    errors,
    handleFieldChange,
  ]);

  return (
    <BaseAdminScreen
      screenName="CreateResource"
      navigation={navigation}
      showPlaceholder={false}
      scrollEnabled={false}
      contentContainerStyle={styles.baseScreenContent}
    >
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {scrollContent}
        </ScrollView>

        <ActionButtons
          isLoading={isLoading}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          submitText="Create"
        />
      </KeyboardAvoidingView>
    </BaseAdminScreen>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 20,
    flexGrow: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
  },
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100, // Space for action buttons
  },
});
